// src/services/storage.ts
import { ext } from '../utils/browserApi';
import { log, warn, error as logError } from '../utils/logger';

/**
 * Storage helpers
 * - Keeps synchronous localStorage API for callers (backwards compatible)
 * - When running as an extension and browser.storage.local is available, we mirror writes
 *   to browser.storage.local asynchronously to improve isolation (best-effort).
 * - Loading is synchronous (returns localStorage value) for compatibility; we also
 *   attempt an async read from browser.storage.local and overwrite localStorage if the
 *   latter is stale (best-effort background sync).
 */

const isExtStorageAvailable = (): boolean => {
  try {
    return Boolean((ext as any)?.storage?.local);
  } catch {
    return false;
  }
};

// Async API: returns promises and performs browser.storage.local operations when available
export const saveToStorageAsync = async <T>(key: string, value: T): Promise<void> => {
  const serializedValue = JSON.stringify(value);
  try {
    localStorage.setItem(key, serializedValue);
    log(`Saved to localStorage: ${key}`);
  } catch (e) {
    logError(`Error saving to localStorage for key ${key}:`, e);
  }

  if (isExtStorageAvailable()) {
    try {
      await new Promise<void>((resolve) => {
        ext.storage.local.set({ [key]: serializedValue }, () => {
          if (ext.runtime?.lastError) {
            warn(`storage.local.set failed for key ${key}:`, ext.runtime.lastError);
          } else {
            log(`Mirrored key to storage.local: ${key}`);
          }
          resolve();
        });
      });
    } catch (err) {
      warn(`Error mirroring to storage.local for key ${key}:`, err);
    }
  }
};

export const loadFromStorageAsync = async <T>(key: string, defaultValue: T): Promise<T> => {
  try {
    const serializedValue = localStorage.getItem(key);
    if (serializedValue !== null) {
      const parsedValue = JSON.parse(serializedValue) as T;
      log(`Loaded from localStorage: ${key}`);
      return parsedValue;
    }

    // Attempt to read from browser.storage.local (async) and hydrate localStorage
    if (isExtStorageAvailable()) {
      try {
        const res: any = await new Promise((resolve) => ext.storage.local.get([key], resolve));
        const val = res && res[key];
        if (val) {
          try {
            localStorage.setItem(key, val);
            log(`Hydrated localStorage from storage.local for key ${key}`);
            return JSON.parse(val) as T;
          } catch (err) {
            warn('Failed to write hydrated value into localStorage:', err);
          }
        }
      } catch (err) {
        warn('Error reading from storage.local:', err);
      }
    }

    log(`No value in storage for key ${key}, returning default`);
    return defaultValue;
  } catch (e) {
    logError(`Error loading from storage for key ${key}:`, e);
    return defaultValue;
  }
};

// ── Account-scoped storage contract ──────────────────────────────────────────
// Every persisted key is either USER-scoped (derived from / owned by the
// signed-in account) or DEVICE-scoped (a property of this install, valid for
// whoever is looking at the screen). User-scoped keys must never survive an
// account switch — TestFlight build 6 greeted account B with account A's name
// because `name` is device-global and sign-out never touched it.
//
// Device-scoped (intentionally NOT listed): 'darkMode' (theme), 'todosViewMode'
// (drawer view), 'nowBlockExpanded', 'notes-theme', 'dev_locale' — pure UI
// preferences with no identity content. 'tour.explore.v1' (first-run Explore
// Mode progress) is device-scoped too: it records what THIS install has been
// shown, so a second account on the same device is not re-onboarded.
// 'upgradeCard.openDays' / 'upgradeCard.snoozedUntil' (in-app upgrade card)
// are device-scoped for the same reason.

/**
 * Marks which account's user-scoped data currently lives on this device.
 * Stamped on every sign-in; compared on auth-state change (AppContext identity
 * guard). Deliberately NOT cleared on sign-out — it records data ownership,
 * not session state, so a same-account re-sign-in can resume local-only data
 * (streak) while a different account still triggers a purge.
 */
export const LAST_AUTH_UID_KEY = 'auth.lastUid';

/**
 * The account whose user-scoped data lives on this device, read synchronously
 * from the primary store. AppContext's identity guard stamps it on sign-in
 * BEFORE it purges the previous account's keys, so a reader that runs after
 * the switch already sees the new owner.
 */
export const readDataOwnerUid = (): string | null => {
  try {
    const raw = localStorage.getItem(LAST_AUTH_UID_KEY);
    const v: unknown = raw ? JSON.parse(raw) : null;
    return typeof v === 'string' && v !== '' ? v : null;
  } catch {
    return null;
  }
};

// ── Owner-stamped caches ─────────────────────────────────────────────────────
// Clearing user-scoped keys on sign-out is not enough on its own: a reader can
// run before the identity guard's purge (child effects fire before the
// provider's), or hydrate from browser.storage.local while its async remove is
// still pending. So user-scoped caches carry the uid that wrote them, and a
// reader never gets back another account's value.

interface OwnedEnvelope {
  owner: string;
  value: unknown;
}

const isOwnedEnvelope = (v: unknown): v is OwnedEnvelope =>
  typeof v === 'object' && v !== null && typeof (v as OwnedEnvelope).owner === 'string' && 'value' in v;

/** Persist `value` stamped with the account that owns it. */
export const saveOwnedToStorageAsync = <T>(key: string, owner: string, value: T): Promise<void> =>
  saveToStorageAsync<OwnedEnvelope>(key, { owner, value });

/**
 * The stored value when `owner` wrote it; otherwise null (missing, unstamped,
 * or another account's — the latter is also deleted). The value is `unknown`:
 * callers still validate its shape.
 */
export const loadOwnedFromStorageAsync = async (key: string, owner: string | null): Promise<unknown> => {
  if (!owner) return null;
  const raw = await loadFromStorageAsync<unknown>(key, null);
  if (raw === null) return null;
  if (isOwnedEnvelope(raw) && raw.owner === owner) return raw.value;
  void removeFromStorageAsync(key);
  return null;
};

/**
 * User-scoped keys that are re-hydratable from the account itself (backend
 * profile / todos sync / Firebase displayName). Cleared on sign-out AND on
 * sign-in with a different uid. Owners: AppContext ('name'…'prefsLocalUpdatedAt'),
 * QuranVerse/FavoritesPanel ('favorite.verse.map' — cache of backend favorites),
 * useIntegrations ('rhemn:integrations:v1' — cache of integrationAccounts).
 */
export const USER_SCOPED_STORAGE_KEYS: readonly string[] = [
  'name',
  'focus',
  'focusCompleted',
  'todos',
  'todoLists',
  'activeListId',
  'pendingTodoMutations',
  'prefsLocalUpdatedAt',
  'prayerCalculationMethod',
  'weatherUnit',
  'favorite.verse.map',
  // Calendars & tasks: the last-known connected accounts + rollout flag
  // (useIntegrations). Re-fetched from integrationAccounts on every load.
  'rhemn:integrations:v1',
  // Calendars (P3): today's events cached on the device so the day paints
  // instantly. The privacy policy promises it is removed at sign-out, so it
  // is user-scoped from the first commit that could write it. Any other
  // calendar/event/reminder cache must be listed here too
  // (src/__tests__/privacyInvariants.test.ts enforces the naming rule).
  'rhemn:calendarEvents:v1',
  // Calendar & Reminders (Apple, native only): this device's choices — which
  // calendars and lists, prayer-times switch (features/Calendar/appleSettings).
  'rhemn:appleCalendarReminders:v1',
  // Meeting prompts already dismissed or acted on (spec §12.1), per event.
  'rhemn:meetingPrompt:handled:v1',
  // Prayer times written to a calendar: the once-a-day-per-device marker (P5).
  'rhemn:prayerCalendarSync:v1',
];

/**
 * User-scoped but LOCAL-ONLY (no backend copy — clearing is irrecoverable):
 * the streak. Decision: it survives sign-out (the same account signing back in
 * on this device keeps its streak), but it is purged when a DIFFERENT account
 * signs in — account B must never inherit A's streak. Backend follow-up: sync
 * streak state per-user so it can move to the plain list above.
 */
export const USER_SCOPED_LOCAL_ONLY_KEYS: readonly string[] = ['streak.state.v1'];

/**
 * Remove the user-scoped keys from localStorage + the extension storage mirror.
 * `includeLocalOnly` is reserved for account-switch purges (see above); plain
 * sign-out passes nothing. The localStorage removals run synchronously before
 * the first await, so a caller in a React effect can rely on the primary store
 * being clean before the next effect in the same commit reads it.
 */
export const clearUserScopedStorageAsync = async (
  options?: { includeLocalOnly?: boolean },
): Promise<void> => {
  const keys = options?.includeLocalOnly
    ? [...USER_SCOPED_STORAGE_KEYS, ...USER_SCOPED_LOCAL_ONLY_KEYS]
    : [...USER_SCOPED_STORAGE_KEYS];
  // Synchronous primary-store sweep first: removeFromStorageAsync clears
  // localStorage before its first await, but only Promise.all's FIRST callee
  // would run synchronously if we relied on it alone.
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore — removeFromStorageAsync retries below
    }
  }
  await Promise.all(keys.map((key) => removeFromStorageAsync(key)));
};

export const removeFromStorageAsync = async (key: string): Promise<void> => {
  try {
    localStorage.removeItem(key);
    log(`Removed from localStorage: ${key}`);
  } catch (e) {
    logError(`Error removing from localStorage for key ${key}:`, e);
  }

  if (isExtStorageAvailable()) {
    try {
      await new Promise<void>((resolve) => ext.storage.local.remove([key], () => resolve()));
      log(`Removed key from storage.local: ${key}`);
    } catch (err) {
      warn('Error removing from storage.local:', err);
    }
  }
};
